import * as React from "react";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown } from "lucide-react";

interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  value: string | number;
  valueColor?: string;
  label: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  onClick?: () => void;
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      icon,
      iconBgColor = "bg-primary/10",
      iconColor = "text-primary",
      value,
      valueColor,
      label,
      trend,
      onClick,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-xl border bg-card text-card-foreground shadow p-6",
          onClick && "cursor-pointer hover:shadow-md transition-shadow",
          className
        )}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        {...props}
      >
        <div className="flex items-start justify-between">
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-lg",
              iconBgColor
            )}
          >
            <div className={cn("h-6 w-6", iconColor)}>{icon}</div>
          </div>
          {trend && (
            <div
              className={cn(
                "flex items-center gap-1 text-xs font-medium",
                trend.isPositive ? "text-success" : "text-danger"
              )}
            >
              {trend.isPositive ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
        <div className="mt-4">
          <p
            className={cn(
              "text-2xl font-bold tracking-tight",
              valueColor
            )}
          >
            <bdi dir="ltr">{value}</bdi>
          </p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
        </div>
      </div>
    );
  }
);
StatCard.displayName = "StatCard";

export { StatCard };
export type { StatCardProps };
